import { useCallback, useEffect, useState } from 'react';
import { api, ErrorApi, mensajeDe, type Yo } from '../../services/api';
import { alTocar, ir } from '../../components/ruta';
import { Aviso, Cargando, useTema } from '../../components/ui';
import { CambiarPin, Entrar, PrimeraVez } from './Entrada';
import { ListaTorneos } from './ListaTorneos';
import { EditarTorneo } from './EditarTorneo';
import { Organizadores } from './Organizadores';
import { Sitio } from './Sitio';

// El mundo de los organizadores, todo bajo /admin. Siempre en tema FIP: el
// tema de cada torneo se ve en la página pública ("Ver como el público").

type Estado = 'mirando' | 'setup' | 'afuera' | 'adentro' | 'error';

export function Admin({ ruta, onUsuarioChange }: { ruta: string; onUsuarioChange: (usuario: Yo | null) => void }) {
  useTema('fip');
  const [estado, setEstado] = useState<Estado>('mirando');
  const [yo, setYo] = useState<Yo | null>(null);
  const [error, setError] = useState('');

  const revisar = useCallback(async () => {
    try {
      setYo(await api.yo());
      setEstado('adentro');
    } catch (e) {
      if (!(e instanceof ErrorApi) || e.estado !== 401) {
        setError(mensajeDe(e));
        setEstado('error');
        return;
      }
      setYo(null);
      try {
        setEstado((await api.estadoSetup()).disponible ? 'setup' : 'afuera');
      } catch (e2) {
        setError(mensajeDe(e2));
        setEstado('error');
      }
    }
  }, []);

  useEffect(() => {
    void revisar();
  }, [revisar]);

  useEffect(() => {
    onUsuarioChange(estado === 'adentro' && yo && !yo.debe_cambiar_pin ? yo : null);
    return () => onUsuarioChange(null);
  }, [estado, yo, onUsuarioChange]);

  if (estado === 'mirando') return <Cargando />;
  if (estado === 'error') {
    return (
      <div className="mx-auto max-w-sm p-6">
        <Aviso tono="error">{error}</Aviso>
      </div>
    );
  }
  if (estado === 'setup') return <PrimeraVez alTerminar={revisar} />;
  if (estado === 'afuera' || !yo) return <Entrar alEntrar={revisar} />;
  if (yo.debe_cambiar_pin) return <CambiarPin obligado alTerminar={revisar} />;

  const salir = async () => {
    await api.salir().catch(() => undefined);
    ir('/admin');
    void revisar();
  };

  const torneo = ruta.match(/^\/admin\/t\/(\d+)(?:\/(control|preparar))?$/);
  const seccion = ruta === '/admin/organizadores' ? 'organizadores' : ruta === '/admin/sitio' && yo.es_dueno ? 'sitio' : torneo ? 'torneo' : 'torneos';

  return (
    <div className="mx-auto flex min-h-[calc(100dvh-3.5rem)] max-w-2xl flex-col">
      <header className="border-b border-(--color-borde) bg-(--color-panel) px-4 py-3">
        <nav aria-label="Secciones de administración" className={`grid ${yo.es_dueno ? 'grid-cols-3' : 'grid-cols-2'} gap-2 text-sm`}>
          {[
            { id: 'torneos', nombre: 'Torneos', href: '/admin' },
            { id: 'organizadores', nombre: 'Organizadores', href: '/admin/organizadores' },
            ...(yo.es_dueno ? [{ id: 'sitio', nombre: 'Sitio', href: '/admin/sitio' }] : []),
          ].map(({ id, nombre, href }) => {
            const activa = seccion === id || (id === 'torneos' && seccion === 'torneo');
            return (
              <a
                key={id}
                href={href}
                onClick={alTocar(href)}
                aria-current={activa ? 'page' : undefined}
                className={`flex min-h-11 items-center justify-center rounded-lg border px-1.5 py-2 font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-acento) ${activa ? 'border-(--color-acento) bg-(--color-acento) text-(--color-sobre-acento)' : 'border-(--color-borde) bg-(--color-tarjeta) text-(--color-apagado) hover:border-(--color-acento) hover:text-(--color-texto)'}`}
              >
                {nombre}
              </a>
            );
          })}
        </nav>
      </header>

      <main className={`flex flex-1 flex-col ${seccion === 'torneo' || seccion === 'sitio' ? 'gap-2' : 'gap-4'} px-4 py-4`}>
        {seccion === 'torneos' && <ListaTorneos />}
        {seccion === 'torneo' && <EditarTorneo key={torneo![1]} id={Number(torneo![1])} vista={torneo![2] as 'control' | 'preparar' | undefined} />}
        {seccion === 'organizadores' && <Organizadores yo={yo} />}
        {seccion === 'sitio' && <Sitio />}
      </main>
      <footer className="mx-4 flex items-center justify-between gap-3 border-t border-(--color-borde) py-1 text-xs">
        <a href="/guia-organizador" target="_blank" rel="noopener" className="inline-flex min-h-11 items-center rounded-md text-(--color-acento) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-acento)">
          <span className="inline-flex items-center gap-1.5 rounded-md border border-(--color-borde) px-2 py-1 transition-colors hover:border-(--color-acento) hover:bg-(--color-acento)/10">
            <span aria-hidden="true" className="flex size-3.5 items-center justify-center rounded-full border border-current text-[10px] font-bold">?</span>
            Guía de uso <span aria-hidden="true">↗</span>
          </span>
        </a>
        <button onClick={salir} className="min-h-11 rounded-md px-2 text-(--color-apagado) transition-colors hover:text-(--color-texto) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-acento)">
          Salir
        </button>
      </footer>
    </div>
  );
}
